import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Button } from "reactstrap";

const MENU_MIN_WIDTH = 160;
const MENU_OFFSET = 4;

const ReportsRowActionsMenu = ({
  report,
  onEdit,
  onSchedule,
  onDuplicate,
  onDelete,
  duplicatePending,
  t,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0 });
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const toggleEl = toggleRef.current;
    if (!toggleEl) return;

    const rect = toggleEl.getBoundingClientRect();
    const left = Math.max(8, rect.right - MENU_MIN_WIDTH);
    const top = rect.bottom + MENU_OFFSET;

    setMenuStyle({ top, left });
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (open) {
      closeMenu();
      return;
    }

    updateMenuPosition();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = (event) => {
      const target = event.target;
      if (toggleRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      closeMenu();
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    const handleScrollOrResize = () => {
      closeMenu();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open, closeMenu]);

  const handleItemClick = (action) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeMenu();
    action();
  };

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="dropdown-menu show dtiq-reports-row-actions-menu"
          style={{
            position: "fixed",
            top: menuStyle.top,
            left: menuStyle.left,
            display: "block",
            minWidth: MENU_MIN_WIDTH,
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {report?.is_edit && report?.type !== "standard" && (
            <button
              type="button"
              className="dropdown-item"
              style={{ fontWeight: 500 }}
              onClick={handleItemClick(() => onSchedule(report.id))}
            >
              {t("reports.scheduleReport") || "Schedule Report"}
            </button>
          )}
          {report?.is_edit && (
            <button
              type="button"
              className="dropdown-item"
              style={{ fontWeight: 500 }}
              onClick={handleItemClick(() => onEdit(report.id))}
            >
              {t("reports.editReport") || "Edit Report"}
            </button>
          )}
          <button
            type="button"
            className="dropdown-item"
            style={{ fontWeight: 500 }}
            disabled={duplicatePending}
            onClick={handleItemClick(() => onDuplicate(report.id))}
          >
            {t("reports.duplicateReport") || "Duplicate Report"}
          </button>
          {report?.is_edit && (
            <button
              type="button"
              className="dropdown-item"
              style={{ fontWeight: 500 }}
              onClick={handleItemClick(() => onDelete(report.id))}
            >
              {t("reports.deleteReport") || "Delete Report"}
            </button>
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <Button
        innerRef={toggleRef}
        size="sm"
        color="link"
        className="btn-icon btn-link like btn-round"
        onClick={handleToggleClick}
        style={{
          background: "transparent",
          border: "none",
          boxShadow: "none",
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <i className="fas fa-ellipsis-v" />
      </Button>
      {menu}
    </>
  );
};

export default ReportsRowActionsMenu;
